import React, { useState } from "react";
import {
  UpgradeAccountRequest,
  UpgradeAccountTypes,
  ValidateAccountResponse,
} from "../../types/upgradeAccount";
import apiHooks from "../../customHooks/apis";
import CustomModal from "../Modal/CustomModal";
import PopUp from "../Popup";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import SectionLabel from "../SectionLabel";
import Loader from "../Loader";
import { removeEmptyFields } from "../../utils/helpers";

type ModalAction = "edit" | "cancel" | "proceed" | "cancelled" | "success";
const Summary: React.FC<UpgradeAccountTypes> = ({ setCurrentStep }) => {
  const { useUserDetails, useUpgradeAccount } = apiHooks;
  const { handleUpgradeAccount, upgradingAccount } = useUpgradeAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [modalAction, setModalAction] = useState<ModalAction>(
    "success" as "cancel",
  );
  const stepperData = useAppSelector((state) => state.stepper.data);
  const { userDetails } = useUserDetails<ValidateAccountResponse>();
  const { firstName, lastName, middleName } = userDetails;
  const isTier3 = searchParams.get("type") === "tier3";
  const {
    accountNumber,
    employerAddress,
    employerName,
    ninUrl,
    positionHeld,
    salaryInfo,
    selectEmploymentStatus,
    utilityBill,
    businessAddress,
    natureofBusiness,
  } = stepperData;
  const { ninUrlDocUpload, utilityBillDocUpload, ...rest } = stepperData;

  const handleClickBtn = (action: ModalAction) => {
    if (action === "edit") return setCurrentStep(1);
    setModalAction(action);
  };

  const handleClickModalBtn = (text: string) => {
    if (!text) return;
    const lowerCaseText = text.toLowerCase();
    if (lowerCaseText.includes("yes") && lowerCaseText.includes("cancel")) {
      handleClickBtn("cancelled");
    }
    if (lowerCaseText === "continue") return navigate("/");
  };

  return (
    <div>
      {upgradingAccount && <Loader />}
      <b className="fw-semibold fs-5 text-center lh-sm">Customer information</b>
      <p>Confirm your personal information before proceeding</p>
      <div className="d-grid gap-3">
        <div className="row">
          <SummaryItem
            label={"Account name"}
            value={`${firstName} ${middleName} ${lastName}`.trim()}
            className="col"
          />
          <SummaryItem
            label={"Account Number"}
            value={accountNumber}
            className="col"
          />
        </div>
        <div className="row">
          <SummaryItem
            label={"Nature of business"}
            value={natureofBusiness}
            className="col"
          />
          <SummaryItem
            label={"Business address"}
            value={businessAddress}
            className="col"
          />
        </div>
        <div className="row">
          {/* <SummaryItem label={"Bank Branch"} value={""} className="col" /> */}
          <SummaryItem
            label={"Employment Type"}
            value={selectEmploymentStatus}
            className="col"
          />
          <SummaryItem
            label={"Employer Name"}
            value={employerName}
            className="col"
          />
        </div>
        <div className="row">
          <SummaryItem
            label={"Salary Information"}
            value={salaryInfo}
            className="col"
          />
          <SummaryItem
            label={"Position Held"}
            value={positionHeld}
            className="col"
          />
        </div>
        <div className="row">
          <SummaryItem
            label={"Employer Address"}
            value={employerAddress}
            className="col"
          />
        </div>
      </div>

      <SectionLabel label={"Means of Identification Document"} />
      <div className="row">
        {utilityBill && (
          <div className="col-6">
            <span>Utility Bill </span>
            <img
              src={utilityBillDocUpload}
              alt="Utility Bill"
              className="ratio ratio-16x9"
            />
          </div>
        )}
        {ninUrl && (
          <div className="col-6">
            <span>Front Page NIN Card</span>
            <img
              src={ninUrlDocUpload}
              alt="Front Page NIN Card"
              className="ratio ratio-16x9"
            />
          </div>
        )}
      </div>
      <SectionLabel label="" />
      <div className="d-flex gap-3">
        <button
          className="w-100 bg-transparent border-0 fw-semibold text-center align-middle"
          onClick={() => handleClickBtn("edit")}
        >
          Edit Information
        </button>
        <button
          className="whiteBtn w-100"
          onClick={() => handleClickBtn("cancel")}
        >
          Cancel
        </button>
        <button
          className="coloredBtn w-100"
          onClick={() =>
            handleUpgradeAccount(
              removeEmptyFields(rest) as UpgradeAccountRequest,
              () => setCurrentStep(4),
            )
          }
        >
          Proceed
        </button>
      </div>
      <CustomModal
        modalTitle="Cancel Account Upgrade Process"
        show={modalAction === "cancel"}
        onClickClose={() => handleClickBtn("" as "cancel")}
      >
        <PopUp
          title="Are you sure you want to cancel this account upgrade process?"
          message="This implies that your account would not be upgraded and you would continue with you present account status."
          cancelText="No"
          btnText="Yes, Cancel"
          onClickBtn={handleClickModalBtn}
        />
      </CustomModal>
      <CustomModal
        modalTitle="Cancel Account Upgrade Process"
        show={modalAction === "cancelled"}
        onClickClose={() => handleClickBtn("" as "cancel")}
      >
        <PopUp
          title="Account upgrade cancelled."
          message="Your account upgrade has been cancelled."
          cancelText="Continue"
          onClickBtn={handleClickModalBtn}
        />
      </CustomModal>
    </div>
  );
};

export default Summary;

const SummaryItem: React.FC<{
  label: string;
  value: string;
  className?: string;
}> = ({ label, value, className }) => {
  if (!value) return "";
  return (
    <div className={`d-grid gap-2 ${className || ""}`}>
      <span className="text-secondary">{label}</span>
      <span>{value}</span>
    </div>
  );
};
